import React, { FC, useState, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import classNames from "classnames";
import toast from "react-hot-toast";
import { AxiosRequestConfig } from "axios";
import { IExpertQuestions, IVideoContent } from "./constants";
import { BriefForm, BriefFormHelpers } from "./BriefForm";
import axiosInstant from "../../api";
import { createProjectBrief, editProjectBrief, saveProjectBrief, saveEditProjectBrief, getBrandProject } from "../../api/endpoints";
import { BrandProject } from "../../pages/videoProduction/components/constants";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import { useProcessSiblings } from "../../hooks/useProcessSiblings";
import { useUser } from "../../hooks/useUser";
import { isInternal, resolveProjectBrandId } from "../../utils";
import { ConfirmationDialog } from "../ConfirmationDialog/ConfirmationDialog";
import {
  BRAND_PROJECT_BRIEF_STATUSES,
  describeSharedSiblings,
} from "../../pages/videoProduction/helper";
import { SharedBriefNotice } from "./SharedBriefNotice";
import { IconCross } from "../../utils/icons";
import { TabHeader } from "../Tabs/TabHeader";
import { BriefNotesPanel } from "./notes/BriefNotesPanel";

// Below md the brief and its notes don't fit side by side: one tab each.
const BRIEF_TAB = 0;
const NOTES_TAB = 1;
const PANE_TABS = [
  { value: BRIEF_TAB, text: "Brief" },
  { value: NOTES_TAB, text: "Notes" },
];

interface IProjectItemMenu {
  project: BrandProject;
  onClose: () => void;
  onComplete: (updatedProject?: BrandProject) => void;
  /** Open on the notes thread with this note focused (notification deep link). */
  focusNoteId?: number | string;
}

export const BriefDialog: FC<IProjectItemMenu> = ({
  project,
  onClose,
  onComplete,
  focusNoteId,
}) => {
  // Accumulate Intake (VideoContent) values locally before final submit
  const [apiBody, setApiBody] = useState<Partial<IVideoContent>>({});
  const [submitting, setSubmitting] = useState(false);
  const [showBriefWarningDialog, setShowBriefWarningDialog] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<{
    obj: IExpertQuestions;
    helpers: BriefFormHelpers;
  } | null>(null);

  const { role } = useUser();
  const internalViewer = isInternal(role);
  const sharedProcess = useProcessSiblings(
    project?.order_id != null ? Number(project.order_id) : undefined,
    project?.id,
    { siblingCount: project?.process_project_count }
  );
  const otherProcessProjects = sharedProcess.siblingProjects.filter(
    (p) => Number(p.id) !== Number(project?.id)
  );
  const warnSharedBrief = internalViewer && sharedProcess.isShared;

  useScrollToTop({ target: "dialog" });

  // Brief notes are internal-only (the API 403s brand users): brand viewers
  // get the brief alone, at the dialog's original width.
  const hasNotes = internalViewer;
  const theme = useTheme();
  // noSsr: read the viewport on the first render, so the dialog does not
  // mount in the tab layout and then jump to the split one.
  const isFullScreen = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const isWide = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });
  const isSplit = hasNotes && isWide;
  const [activeTab, setActiveTab] = useState(
    hasNotes && focusNoteId != null ? NOTES_TAB : BRIEF_TAB
  );
  const showBrief = !hasNotes || isSplit || activeTab === BRIEF_TAB;
  const showNotes = hasNotes && (isSplit || activeTab === NOTES_TAB);

  const savedBrief = project?.briefs?.[0];

  const handleVideoContentChange = (data: IVideoContent) => {
    setApiBody((prev) => ({ ...prev, ...data }));
  };

  const [briefAlreadyApproved, setBriefAlreadyApproved] = useState(false);

  const sharedBriefWarning = warnSharedBrief
    ? `Updating the brief will update the brief on other projects that share expert selection: ${describeSharedSiblings(
        otherProcessProjects
      )}.`
    : "";

  const approvedRevertScope = warnSharedBrief
    ? "revert every project that shares this expert selection to Brief Review"
    : "revert the status of this project to Brief Review";
  const approvedWarning = briefAlreadyApproved
    ? `The brief has already been approved, do you wish to update anyway? This action will ${approvedRevertScope}.`
    : "";

  const warningDescription = [sharedBriefWarning, approvedWarning]
    .filter(Boolean)
    .join(" ");

  const handleFormSubmit = async (obj: IExpertQuestions, helpers: BriefFormHelpers) => {
    const latestProject = await fetchUpdatedProject();
    const brief = latestProject?.briefs?.[0] || project?.briefs?.[0];
    const isApproved =
      !!brief?.id && brief?.status === BRAND_PROJECT_BRIEF_STATUSES.APPROVED;
    if (isApproved || warnSharedBrief) {
      setBriefAlreadyApproved(isApproved);
      setPendingFormData({ obj, helpers });
      setShowBriefWarningDialog(true);
      return;
    }

    await submitBrief(obj, helpers);
  };

  const fetchUpdatedProject = async (): Promise<BrandProject | undefined> => {
    try {
      const response = await axiosInstant.get(getBrandProject(project.id));
      return response.data;
    } catch (error) {
      console.error('Failed to fetch updated project:', error);
      return undefined;
    }
  };

  const submitBrief = async (obj: IExpertQuestions, helpers: BriefFormHelpers) => {
    const updatedApiBody = { ...apiBody, ...obj };
    delete updatedApiBody.status;
    setSubmitting(true);
    const [expert_age_min, expert_age_max] = obj.ageRange;
    const apiData = {
      ...updatedApiBody,
      expert_age_min,
      expert_age_max,
      ageRange: undefined,
      isSubmit: true,
    };

    const { id, briefs } = project;
    const brandId = resolveProjectBrandId(project);
    try {
      let payload: AxiosRequestConfig = {
        method: "POST",
        url: createProjectBrief(brandId, id),
      };
      if (briefs?.[0]?.id) {
        payload = {
          method: "PATCH",
          url: editProjectBrief(brandId, id, briefs[0].id),
        };
      }

      const { data: saved } = await axiosInstant({ ...payload, data: apiData });
      const briefId = saved?.id || briefs?.[0]?.id;
      if (briefId) await helpers.uploadAttachments(briefId);

      toast.success(
        `Brief ${briefs?.[0]?.id ? "updated" : "created"}.`
      );

      const updatedProject = await fetchUpdatedProject();
      onComplete(updatedProject);
    } catch (err) {
      toast.error((err as Error)?.message);
      onComplete();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={true}
        onClose={onClose}
        maxWidth={hasNotes ? "lg" : "md"}
        fullWidth
        fullScreen={isFullScreen}
        className="dialog"
        // With notes, a fixed height: the modal itself never scrolls — the
        // brief (left) and the notes list (right) each scroll on their own.
        PaperProps={{
          sx: {
            overflow: "hidden",
            ...(isFullScreen || !hasNotes ? {} : { height: "calc(100% - 64px)" }),
          },
        }}
      >
        <DialogTitle className="!px-6 !py-4 border-b border-border-token">
          <Box className="flex items-center justify-between">
            <Typography variant="h5">
              Project Brief
            </Typography>
            {/* Top-right additional cancel (X) */}
            {/* stroke via currentColor — SVG presentation attributes can't read var() */}
            <Box onClick={onClose} className="cursor-pointer select-none text-text-1">
              <IconCross stroke="currentColor" />
            </Box>
          </Box>
        </DialogTitle>

        {hasNotes && !isSplit && (
          <Box className="shrink-0">
            <TabHeader
              tabs={PANE_TABS}
              selectedTab={activeTab}
              setSelectedTab={setActiveTab}
              containerClassName="px-6"
              variant="underline"
            />
          </Box>
        )}

        <Box className="flex flex-1 min-h-0">
          {/* Brief: BriefForm's DialogContent is this column's scroll area and
              its DialogActions stay pinned under it. On the Notes tab it is
              hidden, never unmounted, so Formik state and queued attachments
              survive a tab switch. overflow-y-auto is a short-viewport
              fallback only: when the shared-brief notice plus the actions
              cannot fit, the whole column scrolls so Save / Submit stay
              reachable; at normal heights nothing overflows it.
              overscroll-contain on it and on BriefForm's DialogContent keeps
              a wheel/touch scroll from chaining to the page behind the modal
              (below 1024px a global overflow-x rule defeats MUI's scroll lock). */}
          <Box
            className={classNames(
              "flex-col flex-1 min-w-0 min-h-0 overflow-y-auto overscroll-contain [&_.MuiDialogContent-root]:overscroll-contain",
              showBrief ? "flex" : "hidden"
            )}
          >
            {warnSharedBrief && (
              <Box className="px-6 pt-5 pb-2 shrink-0">
                <SharedBriefNotice siblings={otherProcessProjects} variant="edit" />
              </Box>
            )}

            <BriefForm
              project={project}
              loading={submitting}
              onCancel={onClose}
              onChange={handleVideoContentChange}
              onSubmit={(vals: any, helpers) => handleFormSubmit(vals, helpers)}
              onSave={async (vals: any, helpers) => {
                const draft = { ...apiBody, ...vals } as any;
                delete draft.status;
                try {
                  setSubmitting(true);
                  const { id } = project;
                  const brandId = resolveProjectBrandId(project);
                  const [expert_age_min, expert_age_max] = vals.ageRange || [];
                  const data = { ...draft, expert_age_min, expert_age_max, ageRange: undefined };
                  const { data: saved } = await axiosInstant({ 
                    method: project?.briefs?.[0]?.id ? 'PATCH' : 'POST', 
                    url: project?.briefs?.[0]?.id ? saveEditProjectBrief(brandId, id, project.briefs[0].id) : saveProjectBrief(brandId, id), 
                    data 
                  });
                  const briefId = saved?.id || project?.briefs?.[0]?.id;
                  if (briefId) await helpers.uploadAttachments(briefId);
                  toast.success('Successfully saved project brief form.');
                  onComplete(await fetchUpdatedProject());
                } catch (e) {
                  toast.error((e as Error)?.message);
                  onComplete();
                } finally {
                  setSubmitting(false);
                }
              }}
            />
          </Box>

          {hasNotes && (
            <Box
              className={classNames(
                // border-t mirrors the top border of BriefForm's DialogContent.
                "flex-col min-w-0 min-h-0 border-t border-border-token",
                isSplit ? "w-[380px] lg:w-[420px] shrink-0 border-l" : "flex-1",
                showNotes ? "flex" : "hidden"
              )}
            >
              <BriefNotesPanel
                projectId={project?.id}
                briefId={savedBrief?.id}
                focusNoteId={focusNoteId}
                showHeader={isSplit}
              />
            </Box>
          )}
        </Box>
      </Dialog>

      <ConfirmationDialog
        open={showBriefWarningDialog}
        onClose={() => {
          setShowBriefWarningDialog(false);
          setPendingFormData(null);
        }}
        onConfirm={() => {
          setShowBriefWarningDialog(false);
          if (pendingFormData) {
            submitBrief(pendingFormData.obj, pendingFormData.helpers);
            setPendingFormData(null);
          }
        }}
        title="Edit Brief"
        description={warningDescription}
        confirmLabel="Update"
        cancelLabel="Cancel"
      />
    </>
  );
};
